/**
 * LocationTextInput - A text input component for location selection
 * Provides a read-only text input that opens a modal when tapped
 */

import { useA11yAutoTextFocus } from './useA11yAutoTextFocus';
import { Box, Flex, Pressable, Text, type BoxRef } from '../../mads';
import { Close, Error } from '../../mads/icons';
import { useFocus } from '../../mads/util/useFocus';
import { useI18n } from '../../shared/I18n';

import { colors } from '@indeed/ifl-tokens';
import { type ReactNode, useCallback, useRef } from 'react';
import {
    Platform,
    StyleSheet,
    TextInput,
    type TextInputProps,
    type ViewStyle,
} from 'react-native';

export interface LocationTextInputProps extends TextInputProps {
    /**
     * This prop enables the clear button
     */
    clearable?: boolean;
    errorMessage?: string | null;
    contentBefore?: ReactNode;
    containerStyle?: ViewStyle;
    inputStyle?: ViewStyle;
    invalid?: boolean;
    /** Accessibility label for the input field */
    ariaLabel?: string;
    /** ID(s) of elements that label this input (for Android accessibility) */
    accessibilityLabelledBy?: string[];
}

export interface CloseIconProps {
    onClear: () => void;
}

/**
 * Text input component for location selection
 * Provides styling and functionality for location inputs
 * including clearable option and error states
 */
export function LocationTextInput({
    clearable = false,
    containerStyle,
    inputStyle,
    onChangeText,
    onFocus,
    onBlur,
    value,
    errorMessage,
    editable = true,
    contentBefore,
    invalid,
    ariaLabel,
    accessibilityLabelledBy,
    placeholderTextColor = colors.foreground.weak,
    ...textInputProps
}: LocationTextInputProps) {
    const i18n = useI18n();
    const { isFocused, handleFocus, handleBlur } = useFocus({
        onFocus,
        onBlur,
    });
    const ref = useRef<TextInput | null>(null);
    const errorColor = colors.foreground.status.critical['[base]'];
    const borderColor =
        invalid || errorMessage
            ? errorColor
            : isFocused
              ? colors.border.primary['[base]']
              : colors.border['[base]'];

    const handleChangeText = useCallback(
        (newText: string) => {
            onChangeText?.(newText);
        },
        [onChangeText],
    );

    const handleClear = useCallback(() => {
        ref?.current?.clear();
        handleChangeText('');
    }, [handleChangeText]);

    const showCloseIcon = clearable && value;

    return (
        <>
            <Box sx={styles.container}>
                {!editable ? (
                    <Pressable
                        onPress={textInputProps.onPress}
                        sx={[styles.textInputContainer, containerStyle]}
                        accessibilityRole="button"
                        accessibilityLabel={
                            accessibilityLabelledBy
                                ? undefined
                                : `${ariaLabel || ''} ${value || ''} ${textInputProps.placeholder || ''}`
                        }
                        accessibilityLabelledBy={accessibilityLabelledBy}
                        accessibilityHint={i18n.gettext('Opens selection modal')}
                    >
                        {contentBefore && (
                            <Box
                                sx={styles.contentBeforeContainer}
                                accessible={false}
                                importantForAccessibility="no-hide-descendants"
                            >
                                <Text level={3}>{contentBefore}</Text>
                            </Box>
                        )}
                        <TextInput
                            style={[
                                styles.textInput,
                                isFocused ? styles.textInputFocused : undefined,
                                { borderColor },
                                inputStyle,
                            ]}
                            importantForAccessibility="no"
                            onChangeText={handleChangeText}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            ref={ref}
                            multiline={false}
                            value={value}
                            editable={editable}
                            placeholderTextColor={placeholderTextColor}
                            {...textInputProps}
                        />
                    </Pressable>
                ) : (
                    <Flex
                        sx={[
                            styles.textInputContainer,
                            containerStyle,
                            isFocused ? styles.textInputFocused : undefined,
                            { borderColor },
                        ]}
                    >
                        {contentBefore && (
                            <Box sx={styles.contentBeforeContainer}>{contentBefore}</Box>
                        )}
                        <TextInput
                            style={[styles.textInput, inputStyle]}
                            onChangeText={handleChangeText}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            ref={ref}
                            multiline={false}
                            value={value}
                            editable={editable}
                            placeholderTextColor={placeholderTextColor}
                            {...textInputProps}
                        />
                        {showCloseIcon ? <CloseIcon onClear={handleClear} /> : null}
                    </Flex>
                )}
            </Box>
            {errorMessage ? <ErrorMessage errorMessage={errorMessage} color={errorColor} /> : null}
        </>
    );
}

export function CloseIcon({ onClear }: CloseIconProps) {
    const i18n = useI18n();

    return (
        <Pressable
            testID={'ClearButton'}
            sx={styles.closeIcon}
            onPress={onClear}
            accessibilityRole="button"
            accessibilityLabel={i18n.gettext('Clear')}
        >
            <Close size="md" color={colors.foreground.strong} aria-hidden />
        </Pressable>
    );
}

export function ErrorMessage({ errorMessage, color }: { errorMessage: string; color: string }) {
    const errorRef = useRef<BoxRef>(null);
    useA11yAutoTextFocus(errorMessage, errorRef);

    return (
        <Box>
            <Flex sx={{ flexDirection: 'row', marginTop: 0 }}>
                <Box
                    ref={errorRef}
                    accessible={true}
                    accessibilityRole="alert"
                    accessibilityLabel={errorMessage}
                    sx={{ flexDirection: 'row', alignItems: 'center' }}
                >
                    <Box sx={{ marginRight: 4, paddingTop: 2 }}>
                        <Error aria-hidden={true} size="xs" color={color} />
                    </Box>
                    <Box sx={{ paddingHorizontal: 8 }}>
                        <Text level={2} sx={{ color: color }}>
                            {errorMessage}
                        </Text>
                    </Box>
                </Box>
            </Flex>
        </Box>
    );
}

const styles = StyleSheet.create({
    container: {
        position: 'relative',
    },
    contentBeforeContainer: { paddingLeft: 4, paddingRight: 6 },
    textInputContainer: {
        textAlignVertical: 'top',
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 10,
    },
    closeIcon: {
        marginVertical: 7,
        marginRight: 12,
        position: 'absolute',
        right: 0,
    },
    textInput: {
        flex: 1,
        fontSize: 16,
        fontFamily: 'Indeed Sans',
        paddingVertical: 12,
        height: 48,
        outlineWidth: Platform.OS === 'web' ? 0 : undefined,
    },
    textInputFocused: {
        boxShadow:
            Platform.OS === 'web'
                ? `${colors.utility.focus['[base]']} 0px -3px 0px 0px inset`
                : undefined,
    },
});
